// IR Blaster (DeviceID 19, and the same-shaped 57 / 62) — the command
// vocabulary, and nothing else.
//
// Ported from the Flutter installer app's lib/pages/remote/ — den_remote.dart,
// ac_remote.dart, tv_remote.dart and light_remote.dart.
//
// ── What a blaster actually is ─────────────────────────────────────────────
// A KEYPAD, not a switch. It has no outputs, no channels and no state: it
// flashes an infrared code at whatever is in front of it and forgets. Four
// different keypads address the SAME device — the Flutter screen hands all four
// tabs one uniqueNumber (remote_main_page.dart:284-318) — and the pad is chosen
// by the two-character prefix on the front of every token.
//
// ⚠️ THE TOKEN IS THE ENTIRE PROTOCOL, AND A WRONG ONE IS NOT AN ERROR.
// There is no IR waveform here, no hex, no Pronto: the app sends a literal
// ASCII word and the firmware (OTABinFileId 7, OTA_AIRCONDITIONER_IR.ino.bin)
// resolves it to a waveform. An unrecognised word is ignored — by the firmware
// silently, and by the appliance necessarily — so a typo does not throw, does
// not log, and does not fail. The television simply does not respond, which
// reads as broken hardware.
//
// The case is NOT uniform and was not tidied on the way across:
//     TVMute  TVSource  TVPre-ch  TVGuide  TVBack   (mixed)
//     TVEXIT  TVUP      TVLEFT    TVAUTO            (upper)
//     RTAllon RTAlloff                              (mixed — NOT RTALLON, and
//                                                    NOT the ALLON/ALLOFF verb
//                                                    used over BLE and by the
//                                                    touch panels)
//     ACHi-Power                                    (mixed)
// Every literal below is quoted from the Dart with its line number. Do not
// "normalise" any of them, and do not invent one: six AC buttons in the Flutter
// UI are built with a null callback and have NO wire token at all
// (ac_remote.dart:466,476,487,488,495,496 — Mode, Fan, Sleep Refresh, Swing
// Direct, Turbo Self Clean, Led Follow Me). They look like an unfinished
// feature and are an absent protocol.
//
// ── Transport ──────────────────────────────────────────────────────────────
// Exactly the transport this app already has. Flutter's single publish path
// (lib/api/smartify_mqtt_api.dart:16-23) builds `[<uno>]<message>#<userId>` on
// topic `<uno>/device` — byte for byte what core/utils/deviceController
// publishCommand() sends. There is no new transport code in this feature.
//
// ── The device does not report ─────────────────────────────────────────────
// ⚠️ NOTHING HERE PARSES STATUS, DELIBERATELY. The Flutter AC pad reads the
// device's `Status` column and takes index 10 as the temperature and index 14
// as the AC model id (timer_comtroller.dart:53-85). Pulled from the live server
// on 2026-08-08, all seven DeviceID-19 rows in the estate — that decode is dead:
//
//   [2927412]0,5,0,0,21,0,0,0,0,1,1,19,111,19,IR BLASTER,2024-12-23T16:51:04.00
//   [925709]TV ON / OFF Done          [6497254]TV ON / OFF Done
//   [2471550]Siren On,10,10,6         [9740309]1-1#Remote
//   [1027335]1-10  (a DIFFERENT unique number, inside device 920687's own row)
//   1-0            (no bracket at all)
//
// Five shapes, and on the one CSV-shaped row index 10 is `1` and index 14 is
// the literal string `IR BLASTER` — a name, not a model id. A temperature dial
// seeded from that shows 1°, clamps to 18, and the user's first touch publishes
// a setpoint they never chose. So the reply is treated as an ACK SENTENCE for
// display and nothing is ever read out of it.

// The pads, in the order the Flutter tab bar shows them
// (remote_main_page.dart:37-42).
export const IR_PADS = ['DEN', 'AC', 'TV', 'LIGHT'];

/** Every DeviceID that is an IR keypad. See the ⚠️ on 58 in deviceMeta. */
export const IR_BLASTER_IDS = [19, 57, 62];

// A key with no wire token — rendered disabled rather than omitted, so the pad
// still looks like the physical remote it copies.
export const NO_TOKEN = null;

// ── DEN set-top box (prefix DN, den_remote.dart) ───────────────────────────
// ⚠️ There is NO DNOFF. Power is the single `DNON` (:454), so the firmware must
// be toggling — which is why sends are rate-limited; see useIrRemote.
export const DEN_KEYS = {
  power: 'DNON', // :454
  digits: {
    1: 'DN1', 2: 'DN2', 3: 'DN3', 4: 'DN4', 5: 'DN5',      // :468,474,480,492,498
    6: 'DN6', 7: 'DN7', 8: 'DN8', 9: 'DN9', 0: 'DN0',      // :504,516,522,528,546
  },
  exit: 'DNEXIT',    // :540
  home: 'DNHOME',    // :552
  back: 'DNBACK',    // :565
  guide: 'DNGUIDE',  // :574
  mute: 'DNMUTE',    // :588
  info: 'DNINFO',    // :597
  volUp: 'DN+VOL',   // :628
  volDown: 'DN-VOL', // :642
  chUp: 'DN+CH',     // :682
  chDown: 'DN-CH',   // :696
};

/**
 * Jump straight to a channel number: `DNCHOICE<n>` (den_remote.dart:135-137).
 *
 * ⚠️ No separator and no padding — `DNCHOICE105`, not `DNCHOICE-105`.
 */
export const denChannel = (channelNumber) => `DNCHOICE${channelNumber}`;

// ── Television (prefix TV, tv_remote.dart) ─────────────────────────────────
// ⚠️ `TVON` is labelled "ON/OFF" in the Dart (:92) and there is no TVOFF
// anywhere: one token, toggling.
export const TV_KEYS = {
  power: 'TVON',        // :92
  mute: 'TVMute',       // :124
  source: 'TVSource',   // :156
  digits: {
    1: 'TV1', 2: 'TV2', 3: 'TV3', 4: 'TV4', 5: 'TV5',      // :169-229
    6: 'TV6', 7: 'TV7', 8: 'TV8', 9: 'TV9', 0: 'TV0',      // :247
  },
  menu: 'TVMenu',       // :265
  prevCh: 'TVPre-ch',   // :271
  search: 'TVSearch',   // :277
  tools: 'TVTools',     // :289
  input: 'TVInput',     // :295
  info: 'TVInfo',       // :301
  sleep: 'TVSleep',     // :313
  zoom: 'TVZoom',       // :319
  hdmi: 'TVHDMI',       // :326
  echo: 'TVECHO',       // :339
  up: 'TVUP',           // :344
  cal: 'TVCAL',         // :352
  left: 'TVLEFT',       // :365
  ok: 'TVOK',           // :389
  right: 'TVRIGHT',     // :405
  auto: 'TVAUTO',       // :419
  down: 'TVDOWN',       // :426
  disp: 'TVDISP',       // :433
  // ⚠️ docs/flutter-reference/02-devices.md said `TVExit`. The code says
  // TVEXIT (:468) and the code is what the firmware matches.
  exit: 'TVEXIT',       // :468
  guide: 'TVGuide',     // :476
  back: 'TVBack',       // :488
  // ⚠️ Not a typo: the Dart builds this one as `'$den-/--'`, so the prefix is
  // followed directly by punctuation (:494).
  dashDash: 'TV-/--',   // :494
  home: 'TVHome',       // :500
  volUp: 'TV+VOL',      // :534
  volDown: 'TV-VOL',    // :548
  fav: 'TVFAV',         // :565 — this one DOES transmit, unlike DEN's FAVO
  chUp: 'TV+CH',        // :593
  chDown: 'TV-CH',      // :607
};

// ── RF lights / fans (prefix RT, light_remote.dart) ────────────────────────
// This pad drives RF sockets and fans rather than an IR appliance, but it is
// the same blaster and the same token protocol.
export const LIGHT_KEYS = {
  mute: 'RTMUTE',       // :101
  fan1Up: 'RT+',        // :136
  fan1Down: 'RT-',      // :150
  fan2Up: 'RTA',        // :211
  fan2Down: 'RTB',      // :225
  allOn: 'RTAllon',     // :174  ⚠️ mixed case, NOT RTALLON
  allOff: 'RTAlloff',   // :182  ⚠️ mixed case, NOT RTALLOFF

  /*
   * ── FAN OFF: NO TOKEN EXISTS, AND ONE MUST NOT BE GUESSED ────────────────
   *
   * The pads draw an Off key between each fan's + and −, because that is where
   * it belongs and where it was asked for. It is disabled until these are
   * filled in, and they are null because the vocabulary genuinely has no word
   * for it — not because nobody got round to it.
   *
   * `light_remote.dart` sends exactly NINE distinct messages, and this is the
   * complete list: RTMUTE (:101), RT+ (:136), RT- (:150), RTAllon (:174),
   * RTAlloff (:182), RTA (:211), RTB (:225), and RT1..RT16 (:245-353). What
   * sits between + and − in the Flutter layout is a `Text("FAN 1")` label
   * (:139-142) — a caption, not a control.
   *
   * ⚠️ Guessing `RT0`, `RTOFF` or `RTFan1Off` would not fail loudly. The
   * firmware ignores a word it does not know, the fan keeps turning, and it
   * reads as broken hardware — the exact failure this whole module is written
   * to prevent. Three candidates worth ASKING the firmware side about, in
   * order of likelihood:
   *     • RTMUTE may already BE "fans off" — its Flutter icon is volume_off,
   *       which is a stand-in, and there is one of it for two fans
   *     • repeated RT- / RTB may step down to a stop
   *     • a real per-fan token that only exists in the firmware source
   *
   * When the word is known, put it here and the key lights up. Nothing else
   * changes.
   */
  fan1Off: null,
  fan2Off: null,
};

/** RT1..RT16, the sixteen RF channels (light_remote.dart:245-353). */
export const LIGHT_CHANNELS = Array.from({ length: 16 }, (_, i) => `RT${i + 1}`);

// ── Air conditioner (prefix AC, ac_remote.dart) ────────────────────────────
export const AC_KEYS = {
  on: 'ACON',              // :400
  off: 'ACOFF',            // :404
  hiPower: 'ACHi-Power',   // :479 — label "Power Chill / Hi Power"
};

// The dial's range, straight from the Flutter slider (ac_remote.dart:429-430).
export const AC_TEMP_MIN = 18;
export const AC_TEMP_MAX = 30;

/**
 * Set the AC setpoint: `Write65-<°C>`.
 *
 * ⚠️ SENT ON RELEASE, NEVER WHILE DRAGGING. The Dart's `onChange` is an empty
 * body and only `onChangeEnd` publishes (ac_remote.dart:435, 442-444). Firing
 * per pixel would blast a dozen different setpoints at the appliance, and since
 * it never replies there is no way to tell which one it took.
 */
export function acTemperature(celsius) {
  const n = Math.round(Number(celsius));
  if (!Number.isFinite(n) || n < AC_TEMP_MIN || n > AC_TEMP_MAX) return null;
  return `Write65-${n}`;
}

/**
 * Select the AC codeset (which brand's IR language to speak): `Write69-<id>`
 * (ac_setting._page.dart:281).
 *
 * ⚠️ THIS IS STORED ON THE DEVICE, not on the server — nothing is written to
 * any table. It is also fire-and-forget with no ack, so a blaster that is
 * offline swallows it: publishing to a disconnected MQTT client is not an error
 * (see the offline note in CLAUDE.md).
 */
export function acCodeset(modelId) {
  const n = Number(modelId);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `Write69-${n}`;
}

/*
 * ── Deliberately NOT ported ────────────────────────────────────────────────
 *
 * THE AC TIMER. `Write51-<a>,52-<b>,…,62-<k>` followed by a bare `TIMEUPDATED`
 * (ac_remote.dart:323-327). Two reasons, either sufficient:
 *   • the dialog pre-fills those eleven fields by READING the status CSV — the
 *     decode shown dead at the top of this file — so every untouched field
 *     would be re-sent as whatever the misparse produced.
 *   • it is not an IR verb at all. The identical frame is sent by an RF sliding
 *     gate (rf_sliding_gate.dart:349) and by the shared timer dialog
 *     (timer_dialog.dart:286): it is a generic firmware config-register write.
 * If scheduling an AC is wanted, this app already has Schedules.
 *
 * THE SIX DEAD AC BUTTONS. Mode, Fan, Sleep Refresh, Swing Direct, Turbo Self
 * Clean, Led Follow Me — null callbacks, no tokens. Rendered disabled here so
 * the pad still matches the physical remote, but there is nothing to send.
 */

/** Every token this module can emit — the list the tests pin. */
export const ALL_TOKENS = [
  DEN_KEYS.power, ...Object.values(DEN_KEYS.digits),
  DEN_KEYS.exit, DEN_KEYS.home, DEN_KEYS.back, DEN_KEYS.guide, DEN_KEYS.mute,
  DEN_KEYS.info, DEN_KEYS.volUp, DEN_KEYS.volDown, DEN_KEYS.chUp, DEN_KEYS.chDown,
  ...Object.values(TV_KEYS).flatMap((v) => (typeof v === 'string' ? [v] : Object.values(v))),
  ...Object.values(LIGHT_KEYS), ...LIGHT_CHANNELS,
  ...Object.values(AC_KEYS),
].filter(Boolean);

const IR_KEYS = {
  DEN_KEYS, TV_KEYS, LIGHT_KEYS, AC_KEYS,
};
export default IR_KEYS;
